/**
 * Custom Events Tracking — core.
 *
 * Pushes the Whittard custom events onto window.dataLayer for GTM/GA4, per the
 * "Custom Events Tracking" spec (2026-08-26). Elevar continues to own every
 * native Online Sales event; nothing here duplicates those.
 *
 * Payload shape (spec "Code examples"):
 *   with parameters → { event: 'header_click', header_click_properties: { element: 'logo' } }
 *   no parameters   → { event: 'navigation_open' }
 * Two events deliberately break that shape because they are native GA4 events —
 * search_term (flat `search_term` key) and the ecommerce/impressions events,
 * which are pushed raw. See pushRaw() and assets/analytics-product.js.
 *
 * Global utility: no section owns site-wide tracking, and every hook here is
 * either a document-level delegated listener or an observer, so this is loaded
 * once from layout/theme.liquid rather than per section.
 *
 * Declarative hooks: any element carrying data-track-event fires that event on
 * click, with every other data-track-* attribute becoming an event parameter
 * (data-track-element="logo" → { element: 'logo' }). Adding an event to new
 * markup needs no change to this file.
 *
 * Companion files, both of which consume window.WhittardAnalytics from here and
 * must therefore load after it (guaranteed by `defer` in layout/theme.liquid):
 *   assets/analytics-product.js — product list, quick view, bundle,
 *     subscription, notify-me, reviews, click & collect
 *   assets/analytics-collection.js — filters, sort and pagination
 *   assets/analytics-forms.js — form_error, including forms inside shadow roots
 *   assets/analytics-country.js — the Orbe geolocation modal and country
 *     selector, split out because none of that DOM belongs to the theme
 */
(function () {
  'use strict';

  var W = window;
  /* Refuse a second execution of this file. The `inited` guard below is
     module-scoped, so it cannot see a duplicate <script> include — and every
     hook here would then be registered twice, silently doubling every event.
     Mirrors the dataset.*Ready guards the theme's own controllers use. */
  if (W.__whittardAnalyticsCore) return;
  W.__whittardAnalyticsCore = true;
  W.dataLayer = W.dataLayer || [];

  /* Selectors for third-party DOM the theme does not own — the Orbe country
     selector and modal (consumed by assets/analytics-country.js) and the Yotpo
     UGC gallery. Held here rather than in each consumer so there is a single
     override point: set window.WhittardAnalyticsConfig before this file runs to
     change any of them without a theme deploy. VERIFY THEM AGAINST THE LIVE APP
     DOM IN QA — an app update can rename them at any time. */
  var DEFAULTS = {
    /* Orbe's real DOM, confirmed against the live storefront (QA, 2026-08-28).
       Every country selector — header, footer, drawer — is the same combobox
       button with aria-expanded and a data-value holding the current country;
       the options are li[id^="orbe-country-"] carrying data-country. The `id*=`
       match covers the --header / --footer variants, with the mount-scoped
       selectors as a fallback if the app renames them. */
    orbeToggle: 'button[role="combobox"][id*="md-form__select__country"], [id^="orbe-market-"] [role="combobox"]',
    orbeOption: 'li[id^="orbe-country-"], [data-countryname]',
    /* Orbe's modal is BEM-prefixed `md-modal` (md-modal__formContent__select in
       the live markup), not `orbe-`. The old selector required both "orbe" and
       "modal" in one class attribute and therefore never matched it at all —
       which is why the modal reported no opens, and why a country picked inside
       it was mistaken for a header dropdown (QA, 2026-08-29).

       Matched on the modal's own container, never on `[class*="md-modal"]`:
       there is no bare `md-modal` block, the outermost element is
       md-modal__container, and Orbe reuses md-modal__formContent__select for the
       inline header and footer pickers too. A substring match would classify
       those as "inside the modal" and mistype every one of their interactions.
       .md-modal__dialog is the same element's inner role="dialog" wrapper, kept
       as a second anchor in case the container is ever renamed. */
    orbeModal: '.md-modal__container, .md-modal__dialog, [class*="orbe"][class*="modal"], .orbe-geolocation-modal',
    orbeApply: 'button, [role="button"], a',
    /* Forms the theme does not own, rendered inside a shadow root. Their error
       messages are unreachable from document.querySelectorAll, so
       assets/analytics-forms.js walks into these hosts explicitly. The contact
       page is Shopify Forms (sections/cms-page.liquid already reaches into the
       same host to style it). */
    shadowFormHosts: 'shopify-forms-embed',
    ugcWidget: '.yotpo-pictures-widget'
  };
  var CONFIG = {};
  var overrides = W.WhittardAnalyticsConfig || {};
  Object.keys(DEFAULTS).forEach(function (k) { CONFIG[k] = overrides[k] || DEFAULTS[k]; });

  /* Matches the md breakpoint the rest of the theme branches on in JS
     (snippets/plp-ui.liquid, layout/theme.liquid's header measurement). */
  /* ═══════════════════════════════════════════════
     Core push
  ═══════════════════════════════════════════════ */

  /* QA aid, silent unless switched on. Most of these events fire on links that
     navigate immediately — a country change reloads into the new market, and the
     nav, header and footer events are all on anchors — so the console is cleared
     before anyone can read the push. With
     sessionStorage.whittardAnalyticsDebug = '1' set once, every push is logged
     and appended to a session-persisted list that survives those navigations.
     Read it back with WhittardAnalytics.log(). */
  var DEBUG_KEY = 'whittardAnalyticsDebug';
  var LOG_KEY = 'whittardAnalyticsLog';
  var debugOn = false;
  try { debugOn = W.sessionStorage.getItem(DEBUG_KEY) === '1'; } catch (e) { debugOn = false; }

  function record(payload) {
    if (!debugOn) return;
    try {
      var log = JSON.parse(W.sessionStorage.getItem(LOG_KEY) || '[]');
      log.push({ at: new Date().toISOString().slice(11, 19), path: W.location.pathname, payload: payload });
      W.sessionStorage.setItem(LOG_KEY, JSON.stringify(log.slice(-300)));
    } catch (e) { /* private mode, or the quota is full — never break tracking for a log */ }
    if (W.console) W.console.log('%c▸ ' + payload.event, 'color:#0a7;font-weight:bold', payload);
  }

  function push(name, props) {
    if (!name) return;
    var payload = { event: name };
    if (props) {
      var keys = Object.keys(props).filter(function (k) {
        return props[k] !== undefined && props[k] !== null && props[k] !== '';
      });
      if (keys.length) {
        var clean = {};
        keys.forEach(function (k) { clean[k] = props[k]; });
        payload[name + '_properties'] = clean;
      }
    }
    W.dataLayer.push(payload);
    record(payload);
  }

  /* Native GA4 events (search_term, the ecommerce impressions events) carry no
     *_properties wrapper — they go on the layer exactly as GA expects them. */
  function pushRaw(payload) {
    if (!payload || !payload.event) return;
    W.dataLayer.push(payload);
    record(payload);
  }

  /* ═══════════════════════════════════════════════
     Shared DOM sweep

     Hooks across all three files react to markup arriving late: alerts, Tagalys
     product lists, success panels revealed rather than navigated to, mini-bag rows
     rebuilt on every cart change, the app-injected country selector. Each could own
     an observer on <body>, but each would then receive its own copy of the records
     for the same mutation. One observer serves them all, coalesced so a burst
     costs one pass, not one per record.

     Coalescing is a minimum INTERVAL, not a single animation frame. Measured on
     the homepage, 16s after load: 19,581 mutation records arrive, all childList,
     overwhelmingly third-party widgets inserting nodes. Frame-coalescing still
     let that through as ~1,000 sweeps, and because analytics-product.js reads
     getBoundingClientRect() per tracked list on each sweep, that was 5,413
     forced layout reads. This is what Lighthouse attributes as a forced
     synchronous layout to assets/analytics.js — the top-of-stack frame is this
     dispatcher, not the file doing the read, which is why fixing the read
     ordering in analytics-product.js did not move the attribution (Vervaunt
     UAT item 2, Barrett Ahern, 17 Sept).

     At a 300ms floor the same page does 298 rect reads, a 94.5% reduction,
     with mutation volume unchanged (17,124 records) since we are not changing
     how often the DOM moves, only how often we respond. Homepage TBT median
     fell from 1,022ms to 827ms across six paired runs, PDP from 733ms to
     676ms across two; the remainder of TBT is third-party script evaluation
     this cannot reach.

     300ms is safe for what the sweeps do: they detect newly inserted elements
     to start observing. The IntersectionObserver that fires the actual
     impression is unaffected once an element is registered, and onDomChange
     still runs its callback synchronously at registration, so first paint
     detection is unchanged. NOT reduced below 300ms without re-measuring:
     the win is entirely in how many sweeps the burst collapses into.

     `characterData` is left on the observer deliberately. It contributes zero
     records on this site (19,581 of 19,581 were childList), so removing it
     measured as a no-op, and leaving it keeps this diff to the one change
     that was shown to matter.
  ═══════════════════════════════════════════════ */

  var SWEEP_MIN_INTERVAL = 300;

  var sweeps = [];
  var sweepPending = false;
  var sweepObserving = false;
  var lastSweep = 0;

  function onDomChange(fn) {
    sweeps.push(fn);
    try { fn(); } catch (e) { /* a first run must not stop registration */ }
    if (sweepObserving) return;
    sweepObserving = true;
    new MutationObserver(function () {
      if (sweepPending) return;
      sweepPending = true;
      /* Idle (nothing swept for a while) runs on the next task, so a genuinely
         new element is picked up immediately. Under a burst the wait absorbs
         the rest of the window, collapsing it into one pass. */
      var wait = Math.max(0, SWEEP_MIN_INTERVAL - (Date.now() - lastSweep));
      W.setTimeout(function () {
        sweepPending = false;
        lastSweep = Date.now();
        sweeps.forEach(function (f) {
          try { f(); } catch (e) { /* one sweep must not stop the others */ }
        });
      }, wait);
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  /* currentList and onDomChange are exported so the companion files report the
     same market-neutral path and share this one observer. */
  W.WhittardAnalytics = {
    push: push,
    pushRaw: pushRaw,
    config: CONFIG,
    currentList: currentList,
    onDomChange: onDomChange,
    /* Console helpers for QA — see the DEBUG_KEY note above. */
    debug: function (on) {
      debugOn = on !== false;
      try {
        W.sessionStorage.setItem(DEBUG_KEY, debugOn ? '1' : '0');
        if (!debugOn) W.sessionStorage.removeItem(LOG_KEY);
      } catch (e) { /* nothing to persist to */ }
      return debugOn ? 'analytics debug ON — reload, then WhittardAnalytics.log()' : 'analytics debug OFF';
    },
    log: function () {
      var entries = [];
      try { entries = JSON.parse(W.sessionStorage.getItem(LOG_KEY) || '[]'); } catch (e) { entries = []; }
      if (W.console && W.console.table) {
        W.console.table(entries.map(function (r) {
          return { time: r.at, event: r.payload.event, path: r.path };
        }));
      }
      return entries;
    }
  };

  /* ═══════════════════════════════════════════════
     Declarative click hooks — [data-track-event]
  ═══════════════════════════════════════════════ */

  /* Events whose `path` parameter is the clicked link's own destination. Derived
     here rather than duplicated into a data-track-path attribute on every link in
     the nav and footer. */
  var PATH_FROM_HREF = { navigation_click: 1, footer_click: 1 };

  /* Market root: '/' on a single-market storefront, '/en-de'-style when a market
     is served from a subfolder (layout/theme.liquid renders routes.root_url).
     Stripping it keeps one collection as one row in GA instead of splitting it
     per market, and is a no-op when the root is '/'. */
  var ROOT = (document.body && document.body.getAttribute('data-root-url')) || '/';

  function normalisePath(pathname) {
    if (ROOT === '/' || !pathname) return pathname;
    if (pathname === ROOT) return '/';
    if (pathname.indexOf(ROOT + '/') === 0) return pathname.slice(ROOT.length);
    return pathname;
  }

  function pathOf(el) {
    var a = el.tagName === 'A' ? el : el.closest('a[href]');
    if (!a) return '';
    try {
      var u = new URL(a.getAttribute('href'), W.location.origin);
      return normalisePath(u.pathname) + u.search;
    } catch (e) {
      return a.getAttribute('href') || '';
    }
  }

  /* The current page as a market-neutral path — the `list` an impression was
     seen on. Shared with assets/analytics-product.js via WhittardAnalytics. */
  function currentList() {
    return normalisePath(W.location.pathname);
  }

  function propsFrom(el) {
    var out = {};
    var attrs = el.attributes;
    for (var i = 0; i < attrs.length; i++) {
      var name = attrs[i].name;
      if (name.indexOf('data-track-') !== 0) continue;
      var key = name.slice('data-track-'.length).replace(/-/g, '_');
      if (key === 'event') continue;
      out[key] = attrs[i].value;
    }
    return out;
  }

  document.addEventListener('click', function (e) {
    var el = e.target.closest('[data-track-event]');
    if (!el) return;
    var name = el.getAttribute('data-track-event');
    var props = propsFrom(el);
    if (PATH_FROM_HREF[name] && !props.path) props.path = pathOf(el);
    push(name, props);
  }, true);

  /* ═══════════════════════════════════════════════
     Navigation — navigation_open
     Two separate signals, because the two navigations are separate controllers:
     the desktop mega sets [data-header][data-mega-open] (it opens on hover and
     on keyboard focus, so there is no single click to listen for), while the
     mobile drawer only ever opens from the burger and deliberately *removes*
     that same attribute (snippets/menu-drawer.liquid — it would flash the cream
     fill over the transparent header). navigation_click is declarative — see
     the nav markup.
  ═══════════════════════════════════════════════ */

  function watchNavigation() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-drawer-open="menu-drawer"]')) push('navigation_open');
    }, true);

    var header = document.querySelector('[data-header]');
    if (!header) return;
    var wasOpen = header.hasAttribute('data-mega-open');
    new MutationObserver(function () {
      var isOpen = header.hasAttribute('data-mega-open');
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;
      if (isOpen) push('navigation_open');
    }).observe(header, { attributes: true, attributeFilter: ['data-mega-open'] });
  }

  /* ═══════════════════════════════════════════════
     Search — search_open, search_term
  ═══════════════════════════════════════════════ */

  var SEARCH_DEBOUNCE = 800;

  function watchSearch() {
    /* Every search entry point (desktop header, mobile search row, PLP) opens the
       same drawer through drawer.js's [data-drawer-open] contract, so this
       catches all of them without touching each trigger. */
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-drawer-open="mini-search-drawer"]');
      if (t) push('search_open');
    }, true);

    /* Delegated rather than bound to the element: the drawer is portalled to
       <body> by drawer.js, and the search page also renders a plain no-JS
       fallback form (sections/search.liquid) that is used if the drawer's
       auto-open fails. Both are covered by one listener, and neither has to
       exist at init. */
    var timer = null;
    var lastSent = '';
    document.addEventListener('input', function (e) {
      var input = e.target.closest('#mini-search-input, input[name="q"]');
      if (!input) return;
      W.clearTimeout(timer);
      timer = W.setTimeout(function () {
        var term = (input.value || '').trim();
        /* "Stops writing" — not every keystroke. A term is sent once; retyping
           the same string after clearing sends it again only if it changed. */
        if (term.length < 2 || term === lastSent) return;
        lastSent = term;
        pushRaw({ event: 'search_term', search_term: term });
      }, SEARCH_DEBOUNCE);
    }, true);
  }

  /* ═══════════════════════════════════════════════
     Mini Cart — cart_open, cart_close
     drawer:open / drawer:close (assets/drawer.js) are dispatched from the one
     choke point every opening/closing path already flows through — the
     delegated [data-drawer-open] click, window.__drawerOpen() (e.g. the
     post-add-to-cart auto-open in mini-bag-cart-script.liquid), and the
     ?open-drawer= URL redirect (layout/theme.liquid) — so this fires for
     every way the Mini Cart can open or close, not just a click on its own
     trigger, per spec ("either automatically or due to an action"). No
     properties on either event, so push() is called with none.
  ═══════════════════════════════════════════════ */

  function watchMiniBag() {
    document.addEventListener('drawer:open', function (e) {
      if (e.detail && e.detail.id === 'mini-bag') push('cart_open');
    });
    document.addEventListener('drawer:close', function (e) {
      if (e.detail && e.detail.id === 'mini-bag') push('cart_close');
    });
  }

  /* ═══════════════════════════════════════════════
     UGC (Yotpo) — ugc_click
     The Visual UGC gallery renders inside the theme's own container
     (snippets/product-visual-gallery.liquid), so its clicks are observable.
  ═══════════════════════════════════════════════ */

  function watchUgc() {
    document.addEventListener('click', function (e) {
      if (e.target.closest(CONFIG.ugcWidget)) push('ugc_click');
    }, true);
  }

  /* ═══════════════════════════════════════════════
     Init
  ═══════════════════════════════════════════════ */

  /* Each watcher is isolated: tracking is not worth breaking, and without this
     a single throw anywhere in init silently kills every hook registered after
     it — a failure mode that is invisible in the UI and only shows up as
     missing data weeks later in GA. */
  function safe(name, fn) {
    try {
      fn();
    } catch (e) {
      if (W.console) W.console.warn('[analytics] ' + name + ' failed to initialise', e);
    }
  }

  /* Idempotent by design. Double-counted events are the one analytics failure
     that never shows up in the UI and silently corrupts every report built on
     them, so re-entry is refused rather than trusted not to happen. Mirrors the
     *ControllerReady guards the theme's own controllers use. */
  var inited = false;

  function init() {
    if (inited) return;
    inited = true;
    safe('navigation', watchNavigation);
    safe('search', watchSearch);
    safe('miniBag', watchMiniBag);
    safe('ugc', watchUgc);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
